import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Star, StarOff, Trash2, Edit2, Eye, Copy, ChevronUp, ChevronDown, Users } from 'lucide-react';

/**
 * PresetTable - 정렬 가능한 프리셋 테이블 컴포넌트
 *
 * @param {Array} presets - 프리셋 목록
 * @param {boolean} showBookmark - 북마크 버튼 표시 여부
 * @param {boolean} isMyPresetTable - 내 프리셋 테이블 여부 (삭제/공개 토글 표시)
 * @param {boolean} showCopyButton - 복사 버튼 표시 여부
 * @param {boolean} showEditButton - 수정 버튼 표시 여부
 * @param {Function} onToggleBookmark - 북마크 토글 함수 (presetId, isCurrentlyBookmarked) => void
 * @param {Function} onPreview - 미리보기 함수 (presetId) => void
 * @param {Function} onCopy - 복사 함수 (presetId) => void
 * @param {Function} onEdit - 수정 함수 (presetId) => void
 * @param {Function} onDelete - 삭제 함수 (presetId) => void
 * @param {Function} onTogglePublish - 공개 상태 토글 함수 (presetId, isCurrentlyPublished) => void
 * @param {Function} onViewExams - 시험 정보 조회 함수 (presetId, presetName) => void
 */
const PresetTable = ({
  presets,
  showBookmark = true,
  isMyPresetTable = false,
  showCopyButton = false,
  showEditButton = false,
  highlightPresetId = null,
  onToggleBookmark,
  onPreview,
  onCopy,
  onEdit,
  onDelete,
  onTogglePublish,
  onViewExams
}) => {
  const highlightRef = useRef(null);
  const [isHighlightVisible, setIsHighlightVisible] = useState(!!highlightPresetId);

  // 하이라이팅된 행으로 자동 스크롤 + 일시적 페이드아웃
  useEffect(() => {
    if (highlightPresetId && highlightRef.current) {
      setIsHighlightVisible(true);
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setIsHighlightVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightPresetId]);
  // 정렬 상태: order가 null이면 기본 순서 (정렬 없음)
  const [sortConfig, setSortConfig] = useState({ field: null, order: null });

  // 난이도 포맷팅
  const renderDifficulty = (difficulty) => {
    return (difficulty / 100).toFixed(2);
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // 정렬 핸들러: desc → asc → null (기본) 순환
  const handleSort = (field) => {
    setSortConfig(prev => {
      if (prev.field !== field) {
        // 다른 필드 클릭 시 내림차순부터 시작
        return { field, order: 'desc' };
      }
      // 같은 필드 클릭 시 순환: desc → asc → null
      if (prev.order === 'desc') {
        return { field, order: 'asc' };
      }
      if (prev.order === 'asc') {
        return { field: null, order: null };
      }
      // null → desc
      return { field, order: 'desc' };
    });
  };

  // 정렬된 프리셋 목록
  const sortedPresets = useMemo(() => {
    if (!presets || presets.length === 0) return [];

    // 정렬 없음 (기본 순서)
    if (!sortConfig.field || !sortConfig.order) {
      return [...presets];
    }

    return [...presets].sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];

      // 날짜 필드는 Date 객체로 변환하여 비교
      if (sortConfig.field === 'updated_at' || sortConfig.field === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // 문자열은 localeCompare 사용
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.order === 'asc'
          ? aVal.localeCompare(bVal, 'ko')
          : bVal.localeCompare(aVal, 'ko');
      }

      // 숫자 및 기타
      if (sortConfig.order === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [presets, sortConfig]);

  // 정렬 아이콘 렌더링
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field || !sortConfig.order) {
      return <span className="ml-1 text-gray-300">↕︎</span>;
    }
    return sortConfig.order === 'asc'
      ? <ChevronUp className="ml-1 inline size-3 text-primary" />
      : <ChevronDown className="ml-1 inline size-3 text-primary" />;
  };

  if (!presets || presets.length === 0) {
    return <p className="py-4 text-center text-sm text-gray-500">데이터 없음</p>;
  }

  // 테이블 컬럼 정의: 최소 폭 지정 + 비율 배분
  const columns = [
    { key: 'preset_id', label: 'CID', sortable: true, className: 'min-w-12 w-[8%]' },
    { key: 'preset_name', label: '커스텀이름', sortable: true, className: 'min-w-48 w-auto' },
    { key: 'author_name', label: '작성자', sortable: true, className: 'min-w-16 w-[10%]' },
    { key: 'length', label: '문제수', sortable: true, className: 'min-w-14 w-[8%] text-right' },
    { key: 'difficulty', label: '난이도', sortable: true, className: 'min-w-14 w-[8%] text-center' },
    { key: 'updated_at', label: '수정일', sortable: true, className: 'min-w-[7rem] w-[13%]' },
    { key: 'actions', label: '작업', sortable: false, className: 'min-w-28 w-[16%]' }
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-100">
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={`whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700 ${col.className} ${
                  col.sortable ? 'cursor-pointer select-none hover:bg-gray-200' : ''
                }`}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                {col.label}
                {col.sortable && <SortIcon field={col.key} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {sortedPresets.map((preset) => {
            const isHighlighted = preset.preset_id === highlightPresetId && isHighlightVisible;
            return (
            <tr
              key={preset.preset_id}
              ref={preset.preset_id === highlightPresetId ? highlightRef : null}
              className="group cursor-pointer transition-colors duration-200 hover:bg-primary/10"
            >
              <td className={`px-3 py-2 text-gray-900 transition-all duration-700 group-hover:rounded-l-3xl ${isHighlighted ? 'bg-primary/10 rounded-l-3xl' : ''}`}>
                <div className="flex items-center gap-1">
                  {showBookmark && (
                    <button
                      className="shrink-0 rounded p-0.5 text-yellow-500 hover:bg-yellow-50 hover:text-yellow-600"
                      onClick={() => onToggleBookmark?.(preset.preset_id, preset.bookmarked)}
                      title="북마크"
                    >
                      {preset.bookmarked ?
                        <Star className="size-4 fill-yellow-500" /> :
                        <StarOff className="size-4" />
                      }
                    </button>
                  )}
                  {preset.preset_id}
                </div>
              </td>
              <td className={`px-3 py-2 transition-all duration-700 ${isHighlighted ? 'bg-primary/10' : ''}`}>
                <div className="flex items-center gap-2">
                  <button
                    className="relative shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    onClick={() => onViewExams?.(preset.preset_id, preset.preset_name)}
                    title="시험 정보"
                  >
                    <Users className="size-4" />
                    {preset.exams_count > 0 && (
                      <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                        {preset.exams_count > 99 ? '99+' : preset.exams_count}
                      </span>
                    )}
                  </button>
                  <span className="line-clamp-1 flex-1 font-medium text-gray-900">{preset.preset_name}</span>
                  {showEditButton && (
                    <button
                      className="shrink-0 rounded p-0.5 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
                      onClick={() => onEdit?.(preset.preset_id)}
                      title="수정"
                    >
                      <Edit2 className="size-4" />
                    </button>
                  )}
                </div>
              </td>
              <td className={`px-3 py-2 text-gray-600 transition-all duration-700 ${isHighlighted ? 'bg-primary/10' : ''}`}>{preset.author_name}</td>
              <td className={`px-3 py-2 text-right text-gray-900 transition-all duration-700 ${isHighlighted ? 'bg-primary/10' : ''}`}>
                <div className="flex items-center justify-end gap-1">
                  {preset.length}
                  <button
                    className="shrink-0 rounded p-0.5 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
                    onClick={() => onPreview?.(preset.preset_id)}
                    title="미리보기"
                  >
                    <Eye className="size-4" />
                  </button>
                </div>
              </td>
              <td className={`px-3 py-2 text-center text-gray-900 transition-all duration-700 ${isHighlighted ? 'bg-primary/10' : ''}`}>{renderDifficulty(preset.difficulty)}</td>
              <td className={`px-3 py-2 text-gray-600 transition-all duration-700 ${isHighlighted ? 'bg-primary/10' : ''}`}>{formatDate(preset.updated_at)}</td>
              <td className={`px-3 py-2 transition-all duration-700 group-hover:rounded-r-3xl ${isHighlighted ? 'bg-primary/10 rounded-r-3xl' : ''}`}>
                <div className="flex items-center gap-1">
                  {/* 공개 토글 (내 프리셋만) */}
                  {isMyPresetTable && (
                    <div className="mr-1 flex items-center">
                      <div className="relative inline-block w-8 select-none align-middle">
                        <input
                          type="checkbox"
                          id={`toggle-table-${preset.preset_id}`}
                          checked={preset.published}
                          onChange={() => onTogglePublish?.(preset.preset_id, preset.published)}
                          className="sr-only"
                        />
                        <label
                          htmlFor={`toggle-table-${preset.preset_id}`}
                          className={`block h-4 cursor-pointer overflow-hidden rounded-full ${
                            preset.published ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          title={preset.published ? '공개됨' : '비공개'}
                        >
                          <span
                            className={`block size-4 rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                              preset.published ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          ></span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* 복사 */}
                  {showCopyButton && (
                    <button
                      className="rounded p-1 text-green-500 hover:bg-green-50 hover:text-green-600"
                      onClick={() => onCopy?.(preset.preset_id)}
                      title="복사"
                    >
                      <Copy className="size-4" />
                    </button>
                  )}

                  {/* 삭제 (내 프리셋만) */}
                  {isMyPresetTable && (
                    <button
                      className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => onDelete?.(preset.preset_id)}
                      title="삭제"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PresetTable;
